/**
 * Format a generated scope as a markdown document for clipboard copy.
 *
 * Per-room blocks grouped by the same sub-heading taxonomy the UI uses
 * (Ceiling → Walls → Trim → ...). Each line item renders as a single
 * pipe-delimited row:
 *
 *   - CAT SEL | activity_code | qty unit | description
 *
 * Reasoning, when present, hangs as a sub-bullet under the line so the
 * receiver can comment on it directly.
 *
 * Trailing blocks per room: Validation Issues (scoped to that room) and
 * No-Match Activities (activities the retrieval couldn't resolve).
 */
import {
  ScopeGenerationResponse,
  ScopeResponseLineItem,
  ScopeResponseRoom,
  ValidationIssue,
  NoMatchItem,
} from '../../types';
import { groupItemsBySection } from './ScopeListView';

const ACTIVITY_TO_SYMBOL: Record<string, string> = {
  replace: '+',
  remove: '-',
  'remove and replace': '&',
  'remove & replace': '&',
  'detach and reset': 'R',
  'detach & reset': 'R',
  'material only': 'M',
  'install only': 'I',
};

function activitySymbol(item: ScopeResponseLineItem): string {
  const key = (item.activity || '').trim().toLowerCase();
  return ACTIVITY_TO_SYMBOL[key] ?? (item.activity || '').trim() ?? '';
}

function amountColumn(item: ScopeResponseLineItem): string {
  if (item.variable) return item.variable;
  if (item.quantity != null) return `${item.quantity} ${item.unit ?? ''}`.trim();
  return `— ${item.unit ?? ''}`.trim();
}

function confidenceSummary(items: ScopeResponseLineItem[]): string {
  const total = items.length;
  const high = items.filter((i) => i.confidence === 'high').length;
  const low = items.filter((i) => i.confidence === 'low').length;
  const parts = [`${total} item${total === 1 ? '' : 's'}`, `${high} high confidence`];
  if (low > 0) parts.push(`${low} needs review`);
  return parts.join(' · ');
}

function lineFor(item: ScopeResponseLineItem): string[] {
  const code = item.xactimate_code ?? '';
  const act = activitySymbol(item);
  const amt = amountColumn(item);
  const desc = item.description ?? '';
  const lines = [`- ${code} | ${act} | ${amt} | ${desc}`];
  const reasoning = (item.reasoning || item.note || '').trim();
  if (reasoning) {
    lines.push(`  - _${reasoning}_`);
  }
  return lines;
}

function roomBlock(
  room: ScopeResponseRoom,
  validationIssues: ValidationIssue[],
): string {
  const sections = groupItemsBySection(room.items);
  const lines: string[] = [
    `# Scope: ${room.room_name}`,
    confidenceSummary(room.items),
    '',
  ];

  for (const group of sections) {
    lines.push(`## ${group.label}`);
    for (const item of group.items) {
      lines.push(...lineFor(item));
    }
    lines.push('');
  }

  // Validation issues scoped to this room
  const roomIssues = validationIssues.filter(
    (v) => !v.room || v.room === room.room_name,
  );
  if (roomIssues.length > 0) {
    lines.push('## Validation Issues');
    for (const v of roomIssues) {
      const tag = `[${v.severity}]`;
      const code = v.xactimate_code ? ` ${v.xactimate_code} —` : '';
      lines.push(`- ${tag}${code} ${v.message}`);
      if (v.suggestion) lines.push(`  - _Suggestion:_ ${v.suggestion}`);
    }
    lines.push('');
  }

  const noMatch: NoMatchItem[] = room.no_match_items ?? [];
  if (noMatch.length > 0) {
    lines.push('## No-Match Activities (manual lookup needed)');
    for (const nm of noMatch) {
      lines.push(`- ${nm.activity_description}`);
      if (nm.reason) lines.push(`  - _${nm.reason}_`);
    }
    lines.push('');
  }

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd();
}

/**
 * Render the entire scope response as a single markdown string. Rooms are
 * separated by horizontal rules so the receiver can easily skim from one
 * room to the next.
 */
export function scopeToMarkdown(scope: ScopeGenerationResponse): string {
  const allIssues = scope.validation_issues ?? [];
  const blocks: string[] = [];

  for (const room of scope.rooms) {
    blocks.push(roomBlock(room, allIssues));
  }

  // General conditions — render as its own pseudo-room so the format
  // stays uniform.
  if (scope.general_conditions && scope.general_conditions.length > 0) {
    const pseudo: ScopeResponseRoom = {
      room_name: 'General Conditions',
      room_type: 'general',
      items: scope.general_conditions,
      notes: [],
    };
    blocks.push(roomBlock(pseudo, allIssues));
  }

  // Global validation issues that didn't carry a room — surface at the end
  // so they don't get lost.
  const orphanIssues = allIssues.filter((v) => !v.room);
  if (orphanIssues.length > 0 && scope.rooms.length === 0) {
    const lines = ['# Validation Issues (no room)'];
    for (const v of orphanIssues) {
      const tag = `[${v.severity}]`;
      const code = v.xactimate_code ? ` ${v.xactimate_code} —` : '';
      lines.push(`- ${tag}${code} ${v.message}`);
    }
    blocks.push(lines.join('\n'));
  }

  return blocks.join('\n\n---\n\n');
}
